import { useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import type { Candidacy, CandidacyOffice, Phase } from "@shared/types";
import { candidacyPath, raceShareUrl } from "@shared/share-links";
import { api } from "../lib/api";
import { useAsync } from "../lib/useAsync";
import { raceBlurb, resolveRacePhase } from "../lib/racePhase";
import { Page, Card, RichLoader, ErrorBox } from "../components/ui";
import { useDelayedFlag } from "../lib/useDelayedFlag";
import { useWaitMessage } from "../lib/useWaitMessage";
import { ClaimBadge, OriginBadge } from "../components/StatusBadge";
import { MeasuresEditor, type DraftMeasure } from "../components/MeasuresEditor";
import { RichTextEditor } from "../components/RichTextEditor";
import { Share } from "../components/Share";
import { usePageTitle } from "../lib/usePageTitle";
import { useI18n, useT } from "../i18n";



function isOffice(v: string): v is CandidacyOffice {
  return v === "congress" || v === "president";
}

/** Group candidacies by party for display. Named parties sort alphabetically;
 * Independent (no party) comes last. Declaration order is kept within a group. */
function groupByParty(candidacies: Candidacy[]): [string, Candidacy[]][] {
  const groups = new Map<string, Candidacy[]>();
  for (const cd of candidacies) {
    const key = cd.party ?? "Independent";
    (groups.get(key) ?? groups.set(key, []).get(key)!).push(cd);
  }
  return [...groups.entries()].sort(([a], [b]) => {
    if (a === "Independent") return 1;
    if (b === "Independent") return -1;
    return a.localeCompare(b);
  });
}

export default function Race() {
  const { t, locale } = useI18n();
  const { countryId = "", office = "" } = useParams();
  const nav = useNavigate();
  // Phase passed by the page that navigated here (Country/Forecast know it);
  // null on a direct deep link, where we fall back to deriving from the date.
  const hintedPhase = (useLocation().state as { phase?: Phase } | null)?.phase ?? null;
  const valid = isOffice(office);
  const { data, loading, error } = useAsync(
    () => (valid ? api.raceCandidacies(countryId, office) : Promise.reject(new Error("Unknown office"))),
    [countryId, office],
  );
  const showRich = useDelayedFlag(loading, 400);
  const waitKey = useWaitMessage(loading);

  const [open, setOpen] = useState(false);
  usePageTitle(
    valid ? t("race.shareTitle", { office: t(office === "president" ? "office.presidentTitle" : "office.congressTitle") }) : null,
  );

  return (
    <Page>
      <Link to={`/c/${countryId}`} className="text-accent text-sm">← {t("race.backToElections")}</Link>

      <div className="flex items-center justify-between gap-2 my-3">
        <h1 className="text-xl font-semibold">
          {valid ? t(office === "president" ? "office.presidentTitle" : "office.congressTitle") : office} — {t("race.candidatesCampaigns")}
        </h1>
        {valid && (
          <Share
            title={t("race.shareTitle", { office: t(office === "president" ? "office.presidentTitle" : "office.congressTitle") })}
            text={t("race.shareText")}
            url={raceShareUrl(countryId, office)}
          />
        )}
      </div>
      {data && (
        <p className="text-sm text-muted -mt-2 mb-4">
          {raceBlurb(
            {
              phase: resolveRacePhase(hintedPhase, data.cycleStartAt),
              cycleStartAt: data.cycleStartAt,
              seats: data.seats,
            },
            t,
            locale,
          )}
        </p>
      )}

      {showRich && <RichLoader headline={t("race.loadingCandidates")} message={t(waitKey)} />}
      {error && <ErrorBox msg={error} />}

      {data && (
        <>
          {data.candidacies.length === 0 ? (
            <p className="text-muted text-sm">{t("race.noDeclared")}</p>
          ) : (
            <div className="space-y-5">
              {groupByParty(data.candidacies).map(([party, members]) => (
                <div key={party}>
                  <h2 className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">{party}</h2>
                  <div className="space-y-3">
                    {members.map((cd) => (
                      <Card key={cd.id} className="cursor-pointer hover:border-accent transition">
                        <Link to={candidacyPath(cd.username, cd.id)} className="block">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold truncate">{cd.username}</span>
                            <span className="flex items-center gap-1.5 shrink-0">
                              <ClaimBadge claimStatus={cd.claimStatus} />
                              <OriginBadge origin={cd.origin} />
                            </span>
                          </div>
                          {cd.intro && <p className="text-sm text-muted mt-1 line-clamp-2">{cd.intro}</p>}
                          <div className="text-xs text-muted mt-2">
                            {t("home.measuresCount", { n: cd.measures.length })}
                          </div>
                        </Link>
                      </Card>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-6">
            {open ? (
              <DeclareForm
                onCancel={() => setOpen(false)}
                onDone={(id, username) => nav(`${candidacyPath(username, id)}?created=1`)}
                submit={(body) => api.declareCandidacy(countryId, office as CandidacyOffice, body)}
              />
            ) : (
              <button onClick={() => setOpen(true)} className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold">
                {t("race.declare")}
              </button>
            )}
          </div>
        </>
      )}
    </Page>
  );
}

function DeclareForm({
  submit,
  onDone,
  onCancel,
}: {
  submit: (body: { username: string; party?: string; introDoc?: string; measures?: { title: string; detailDoc?: string | null }[] }) => Promise<{ candidacy: { id: string; username: string } }>;
  onDone: (candidacyId: string, username: string) => void;
  onCancel: () => void;
}) {
  const t = useT();
  const [username, setUsername] = useState("");
  const [party, setParty] = useState("");
  const [introDoc, setIntroDoc] = useState("");
  const [measures, setMeasures] = useState<DraftMeasure[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function go() {
    setBusy(true);
    setErr(null);
    try {
      const created = await submit({
        username: username.trim(),
        party: party.trim() || undefined,
        introDoc: introDoc || undefined,
        measures: measures.map((m) => ({ title: m.title, detailDoc: m.detailDoc })),
      });
      onDone(created.candidacy.id, created.candidacy.username);
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <Card>
      <h2 className="font-semibold mb-3">{t("race.declare")}</h2>
      <label className="block text-sm mb-1 text-muted">{t("race.form.username")}</label>
      <input
        className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mb-3"
        value={username}
        maxLength={40}
        onChange={(e) => setUsername(e.target.value)}
        placeholder={t("race.form.usernamePlaceholder")}
      />
      <label className="block text-sm mb-1 text-muted">{t("race.form.party")}</label>
      <input
        className="w-full bg-panel2 border border-border rounded px-2 py-1.5 text-sm mb-3"
        value={party}
        maxLength={60}
        onChange={(e) => setParty(e.target.value)}
        placeholder={t("race.form.partyPlaceholder")}
      />
      <label className="block text-sm mb-1 text-muted">{t("race.form.manifesto")}</label>
      <div className="mb-3">
        <RichTextEditor valueDoc={introDoc || null} onChange={setIntroDoc} ariaLabel={t("race.form.manifesto")} />
      </div>
      <label className="block text-sm mb-2 text-muted">{t("race.form.measures")}</label>
      <MeasuresEditor value={measures} onChange={setMeasures} />

      {err && <div className="mt-3"><ErrorBox msg={err} /></div>}
      <p className="text-xs text-muted mt-3">{t("race.form.verifyNote")}</p>
      <div className="flex gap-2 mt-3">
        <button
          disabled={!username.trim() || busy}
          onClick={go}
          className="bg-accent text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
        >
          {busy ? t("common.submitting") : t("common.submit")}
        </button>
        <button onClick={onCancel} className="text-muted text-sm px-3">{t("common.cancel")}</button>
      </div>
    </Card>
  );
}
